import { Box, Card, CardMedia, Typography, Button, Chip, Skeleton } from "@mui/material";
import { useState, useEffect } from "react";
import HeaderAppBar from "./appbar";
import { BackendBaseUrl, ORANGE, ORANGE_HOVER, BLACK, PREMIUM_SHADOW, CARD_SHADOW, BG_LIGHT } from "./global_varibles";
import ScrollToTopButton from "./scrool_button";
import { styled, alpha } from "@mui/material/styles";
import CreateFooterPage from "./footer_page";



// Types
type ServicePrices = Record<string, number>;
type Categories = Record<string, ServicePrices>;

interface PriceItem {
  item_name: string;
  item_url: string;
  categories: Categories;
}

type PriceData = Record<string, PriceItem>;

interface GeneratePriceCardsProps {
  price_data: PriceData;
}
const StyledPriceCard = styled(Card)(({  }) => ({
  backgroundColor: "#fff",
  border: `1px solid ${alpha(ORANGE, 0.1)}`,
  borderRadius: 24,
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  boxShadow: CARD_SHADOW,
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: alpha(ORANGE, 0.3),
    boxShadow: PREMIUM_SHADOW,
  }
}));

const CategoryButton = styled(Button)(({  }) => ({
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 12,
  padding: '8px 20px',
  fontSize: 14,
  transition: 'all 0.2s ease',
  border: `1px solid ${alpha(ORANGE, 0.1)}`,
  color: "#64748b",
  backgroundColor: "#f8fafc",
  '&:hover': {
    borderColor: alpha(ORANGE, 0.3),
    backgroundColor: alpha(ORANGE, 0.05),
    color: ORANGE
  }
}));

const ActiveCategoryButton = styled(CategoryButton)({
  backgroundColor: ORANGE,
  color: '#fff',
  borderColor: ORANGE,
  boxShadow: `0 4px 12px ${alpha(ORANGE, 0.3)}`,
  fontWeight: 700,
  '&:hover': {
    backgroundColor: ORANGE_HOVER,
    borderColor: ORANGE_HOVER,
  }
});

const ServiceRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: `1px solid ${alpha(ORANGE, 0.08)}`,
  transition: 'all 0.2s ease',
  '&:last-child': {
    borderBottom: 'none'
  },
  '&:hover': {
    backgroundColor: alpha(ORANGE, 0.04),
    paddingLeft: theme.spacing(1),
  }
}));

// Generate price cards based on the structured data
function GeneratePriceCards({ price_data }: GeneratePriceCardsProps) {
  const [selectedCategories, setSelectedCategories] = useState<Record<string, string>>({});

  const handleCategoryChange = (itemId: string, category: string) => {
    setSelectedCategories((prevState) => ({
      ...prevState,
      [itemId]: category,
    }));
  };

  return (
    <Box 
      sx={{ 
        width: "100%",
        backgroundColor: BG_LIGHT,
        minHeight: '100vh',
        py: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        position: 'relative'
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.02,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      {/* Header Section */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', mb: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography 
            variant="h3" 
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
              fontWeight: 900,
              color: BLACK,
              mb: 2,
              letterSpacing: '-0.04em'
            }}
          >
            Simple <span style={{ color: ORANGE }}>Pricing</span>
          </Typography>
          <Typography 
            variant="body1"
            sx={{ 
              color: "#64748b",
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: 15, sm: 16 },
              fontWeight: 500
            }}
          >
            Transparent pricing for all your laundry and dry cleaning needs
          </Typography>
        </Box>

        {/* Price Cards Grid */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
            },
            gap: 3,
            mt: 4
          }}
        >
          {Object.keys(price_data).map((item_id) => {
            const item_values = price_data[item_id];
            const categories = item_values.categories;
            const categoryKeys = Object.keys(categories);
            if (categoryKeys.length === 0) return null;

            const selectedCategory = selectedCategories[item_id] ?? categoryKeys[0];
            const services: ServicePrices = categories[selectedCategory] ?? {};

            return (
              <StyledPriceCard key={item_id}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Image Section */}
                  <Box 
                    sx={{ 
                      width: { xs: '100%', sm: 180 },
                      height: { xs: 200, sm: 'auto' },
                      flexShrink: 0,
                      position: 'relative',
                      background: alpha(BLACK, 0.02),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={item_values.item_url}
                      alt={item_values.item_name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))'
                      }}
                    />
                  </Box>

                  {/* Content Section */}
                  <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column' }}>
                    {/* Title */}
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 800,
                        color: BLACK,
                        mb: 2,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {item_values.item_name}
                    </Typography>

                    {/* Category Buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {categoryKeys.map((category) => 
                        selectedCategory === category ? (
                          <ActiveCategoryButton
                            key={category}
                            onClick={() => handleCategoryChange(item_id, category)}
                          >
                            {category}
                          </ActiveCategoryButton>
                        ) : (
                          <CategoryButton
                            key={category}
                            onClick={() => handleCategoryChange(item_id, category)}
                          >
                            {category}
                          </CategoryButton>
                        )
                      )}
                    </Box>

                    {/* Services List */}
                    <Box sx={{ flex: 1 }}>
                      {Object.keys(services).map((service) => {
                        const price = services[service];
                        return price > 0 ? (
                          <ServiceRow key={service}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: "#334155",
                                fontWeight: 600,
                                fontSize: 14
                              }}
                            >
                              {service}
                            </Typography>
                            <Chip 
                              label={`₹${price}`}
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(ORANGE, 0.15),
                                color: ORANGE,
                                fontWeight: 700,
                                fontSize: 13,
                                border: `1px solid ${alpha(ORANGE, 0.3)}`
                              }}
                            />
                          </ServiceRow>
                        ) : null;
                      })}
                    </Box>
                  </Box>
                </Box>
              </StyledPriceCard>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function PriceCards() {
  const [structData, setStructData] = useState<PriceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const getStructuredData = async () => {
    try {
      const bdroute = `${BackendBaseUrl}price`;
      const response = await fetch(bdroute, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const responseData = (await response.json()) as {
        status: string;
        strctured_data?: unknown;
        message?: string;
      };

      if (responseData.status === "success" && responseData.strctured_data) {
        setStructData(responseData.strctured_data as PriceData);
      } else {
        setError("Error in fetching data");
      }
    } catch (err) {
      console.log("Error:", err);
      setError("Server Not Found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStructuredData();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: BG_LIGHT }}>
      <HeaderAppBar />
      
      {loading ? (
        <Box 
          sx={{ 
            backgroundColor: BG_LIGHT,
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}
        >
          <Box sx={{ maxWidth: 1400, width: '100%' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton 
                  key={i}
                  variant="rounded" 
                  height={300}
                  sx={{ 
                    backgroundColor: alpha(ORANGE, 0.03),
                    borderRadius: 4,
                    border: `1px solid ${alpha(ORANGE, 0.08)}`
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : error ? (
        <Box 
          sx={{ 
            backgroundColor: BG_LIGHT,
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}
        >
          <Box 
            sx={{ 
              textAlign: 'center',
              p: 4,
              backgroundColor: '#fff',
              borderRadius: 4,
              border: `1px solid ${alpha(ORANGE, 0.1)}`,
              boxShadow: PREMIUM_SHADOW
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                color: ORANGE,
                fontWeight: 700,
                mb: 2
              }}
            >
              Oops!
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: "#64748b",
                fontWeight: 500
              }}
            >
              {error}
            </Typography>
          </Box>
        </Box>
      ) : structData ? (
        <GeneratePriceCards price_data={structData} />
      ) : null}

      <ScrollToTopButton />
      <CreateFooterPage />
    </Box>
  );
}

export default PriceCards;
